import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { twJoin, twMerge } from 'tailwind-merge';

import { ResourceFields } from '@/data/services/_deprecated/resourceServices';
import Resource, { ResourceTypeEnum } from '@/models/_deprecated/Resource';
import { buildChangedObject } from '@/utils/buildChangedObject';
import { getErrorMessage } from '@/utils/getErrorMessage';
import getKlassViewBaseUrl from '@/utils/getKlassViewBaseUrl';
import alert from '@/utils/shared/alert';
import ResourceCard from '@/components/_deprecated/resource/ResourceCard';
import { UpdateResourceParms } from '../scheduled-lesson/ScheduledLessonResourceContainer';
import { Tooltip } from '@/components/ui/custom/dataDisplay/Tooltip';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { useTranslation } from 'react-i18next';
import { EyeIcon } from '@heroicons/react/24/outline';

type Props = {
  resource: Resource;
  removeResource(resourceId: number): Promise<void>;
  updateResource(params: UpdateResourceParms): Promise<void>;
  loading?: boolean;
  klassId: number;
  lessonId?: number;
  slugCourseName: string;
};

export default function ScheduledLessonResourceEditor(props: Props) {
  const {
    resource,
    updateResource,
    removeResource,
    loading,
    lessonId,
    slugCourseName,
    klassId,
  } = props;

  const { id: resourceId, title } = resource;
  const { t } = useTranslation('translation', {
    keyPrefix: 'common',
  });

  const [editing, setEditing] = useState(resource.uploadingProgress === 100);
  const [visible, setVisible] = useState(false);
  const [resourceType, setResourceType] = useState<ResourceTypeEnum>(
    ResourceTypeEnum.NONE,
  );

  const onEditResource = (type: ResourceTypeEnum) => {
    setResourceType(type);
  };

  const { control } = useForm<ResourceFields>({
    defaultValues: {
      title,
    },
  });

  const onSubmit = async (body: Partial<ResourceFields>) => {
    const changedFields = buildChangedObject<Resource>(resource, body);

    if (Object.keys(changedFields).length) {
      try {
        await updateResource({ resourceId, body: changedFields });
        setEditing(false);
      } catch (error) {
        alert.error(getErrorMessage(error));
      } finally {
        onEditResource(ResourceTypeEnum.NONE);
      }
    }
  };

  const url =
    lessonId &&
    `${getKlassViewBaseUrl({
      lessonId,
      slugCourseName,
      klassId,
    })}/resources/${resource.id}`;

  return (
    <ResourceCard disabled={resource.uploading}>
      <ResourceCard.ModalEdit
        visible={resourceType !== ResourceTypeEnum.NONE}
        resource={resource}
        resourceType={resourceType}
        onClose={() => onEditResource(ResourceTypeEnum.NONE)}
        onSubmit={onSubmit}
        loading={loading}
      />
      <ResourceCard.ModalRemove
        onConfirm={() => removeResource(resourceId)}
        visible={visible}
        onCancel={() => setVisible(false)}
        loading={loading}
      />
      <section
        className={twMerge(
          'flex gap-1 items-center relative w-full',
          editing ? 'flex-col items-start' : '',
        )}
      >
        <section
          className={twJoin(
            'flex items-center gap-2.5',
            editing ? 'w-full' : '',
          )}
        >
          <ResourceCard.Icon resource={resource} />
          <ResourceCard.Title
            control={control}
            editing={editing}
            defaultValue={resource.title}
            loading={resource.uploading}
            onSubmit={onSubmit}
          />
        </section>
        <section className="flex gap-1 items-center h-5">
          <ResourceCard.Preview resource={resource} />
          <ResourceCard.ChangeButton
            resource={resource}
            editing={editing}
            onEdit={onEditResource}
          />
        </section>

        <section
          className={twMerge(
            'flex gap-2.5 absolute right-0',
            editing ? 'bottom-0' : '',
          )}
        >
          <ResourceCard.EditButton
            editing={editing}
            onEdit={() => setEditing(prev => !prev)}
          />
          <ConditionalRenderer
            condition={lessonId}
            fallback={
              <Tooltip text={t('lessonNotAssigned')} enable={true}>
                <EyeIcon className="w-4 h-4 text-primary" />
              </Tooltip>
            }
          >
            {lessonId && url && (
              <ResourceCard.ViewButton
                uploading={resource.uploading}
                url={url}
              />
            )}
          </ConditionalRenderer>
          <ResourceCard.RemoveButton
            onRemove={() => setVisible(prev => !prev)}
          />
        </section>
      </section>
      <ResourceCard.UploadProgress progress={resource.uploadingProgress} />
    </ResourceCard>
  );
}
